import { useState, useCallback, useRef } from 'react';
import hsnService from '../services/hsnService';
import taxesService from '../services/taxesService';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing HSN codes data and operations
 */
const useHsnCodes = () => {
  const [hsnCodes, setHsnCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  // Message alert for success notifications
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  // Deduplication refs
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  // Track in-flight requests to avoid duplicate network calls (useful in StrictMode)
  const inFlightRef = useRef(new Set());

  /**
   * Generate request signature for deduplication
   */
  const generateSignature = useCallback((params) => {
    return JSON.stringify(params || {});
  }, []);

  /**
   * Check if request should be deduplicated
   */
  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  /**
   * Fetch all HSN codes with optional filters
   */
  const fetchHsnCodes = useCallback(async (params = {}, { force = false } = {}) => {
    const signature = generateSignature(params);
    
    if (shouldDedupe(signature, force)) {
      return { success: true, fromCache: true };
    }

    // If an identical request is already in-flight, skip issuing another one
    if (inFlightRef.current.has(signature)) {
      return { success: true, fromCache: true };
    }

    setLoading(true);
    setError(null);
    // mark this signature as in-flight
    inFlightRef.current.add(signature);
    
    try {
      const result = await hsnService.getAllHsnCodes(params);
      
      if (result.success) {
        const { data } = result;
        
        // Handle different response structures
        if (data?.hsnCodes && Array.isArray(data.hsnCodes)) {
          setHsnCodes(data.hsnCodes);
          setTotalPages(data.totalPages || Math.ceil((data.total || data.hsnCodes.length) / (params.limit || 20)));
          setTotalCount(data.total || data.hsnCodes.length);
        } else if (Array.isArray(data)) {
          setHsnCodes(data);
          setTotalPages(1);
          setTotalCount(data.length);
        } else {
          console.warn('Unexpected HSN codes data structure:', data);
          setHsnCodes([]);
          setTotalPages(0);
          setTotalCount(0);
        }
        
        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
        setHsnCodes([]);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.hsnCodes.fetchList');
      setError(errorMessage);
      setHsnCodes([]);
      return { success: false, error: errorMessage };
    } finally {
      // clear in-flight marker
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe]);

  /**
   * Get a specific HSN code by ID
   */
  const getHsnCodeById = useCallback(async (hsnId) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await hsnService.getHsnCodeById(hsnId);
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.hsnCodes.fetchOne');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new HSN code
   */
  const createHsnCode = useCallback(async (hsnData) => {
    setError(null);
    
    try {
      const result = await hsnService.createHsnCode(hsnData);
      
      if (result.success) {
        // Add the new HSN code to the local state
        // If the backend didn't populate gst_rate on create, try to fetch the tax
        const created = { ...(result.data || {}) };
        try {
          const hasGst = typeof created.gst_rate !== 'undefined' && created.gst_rate !== null;
          if ((!hasGst || Number(created.gst_rate) === 0) && created.tax_id) {
            const taxRes = await taxesService.getTaxById(created.tax_id);
            if (taxRes.success && taxRes.data) {
              const t = taxRes.data;
              // Determine tax percentage from common shapes: gst, rate, or cgst+sgst
              const taxPercent = (typeof t.gst !== 'undefined' && t.gst !== null)
                ? Number(t.gst)
                : (typeof t.rate !== 'undefined' && t.rate !== null)
                  ? Number(t.rate)
                  : (typeof t.cgst !== 'undefined' && typeof t.sgst !== 'undefined')
                    ? (Number(t.cgst) + Number(t.sgst))
                    : undefined;

              if (typeof taxPercent !== 'undefined' && !Number.isNaN(taxPercent)) {
                created.gst_rate = taxPercent;
              }
            }
          }
        } catch (e) {
          // If fetching tax fails, we silently proceed with backend data (refresh will correct it)
          // Keep error state unchanged here
        }

        setHsnCodes(prevHsnCodes => [created, ...prevHsnCodes]);
        setTotalCount(prevCount => prevCount + 1);
        
  // Show success message
  const hsnLabel = created.hsn_code || created.code || '';
  showCreate(i18n.t('hsnCodes.notifications.created', { code: hsnLabel }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.hsnCodes.create');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate]);

  /**
   * Update an existing HSN code
   */
  const updateHsnCode = useCallback(async (hsnId, hsnData) => {
    setError(null);
    
    try {
      const result = await hsnService.updateHsnCode(hsnId, hsnData);
      
      if (result.success) {
        // Update the HSN code in local state
        const updated = { ...result.data };
        
        // If tax_id was changed, try to fetch the new tax details to get gst_rate
        try {
          const hasGst = typeof updated.gst_rate !== 'undefined' && updated.gst_rate !== null;
          if ((!hasGst || Number(updated.gst_rate) === 0) && updated.tax_id) {
            const taxRes = await taxesService.getTaxById(updated.tax_id);
            if (taxRes.success && taxRes.data) {
              const t = taxRes.data;
              // Determine tax percentage from common shapes: gst, rate, or cgst+sgst
              const taxPercent = (typeof t.gst !== 'undefined' && t.gst !== null)
                ? Number(t.gst)
                : (typeof t.rate !== 'undefined' && t.rate !== null)
                  ? Number(t.rate)
                  : (typeof t.cgst !== 'undefined' && typeof t.sgst !== 'undefined')
                    ? (Number(t.cgst) + Number(t.sgst))
                    : undefined;

              if (typeof taxPercent !== 'undefined' && !Number.isNaN(taxPercent)) {
                updated.gst_rate = taxPercent;
              }
            }
          }
        } catch (e) {
          // If fetching tax fails, we silently proceed with backend data
        }

        setHsnCodes(prevHsnCodes => 
          prevHsnCodes.map(hsnCode => 
            hsnCode.id === hsnId ? { ...hsnCode, ...updated } : hsnCode
          )
        );
        
  // Show success message
  const updatedLabel = updated.hsn_code || updated.code || '';
  showUpdate(i18n.t('hsnCodes.notifications.updated', { code: updatedLabel }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.hsnCodes.update');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  /**
   * Delete a single HSN code
   */
  const deleteHsnCode = useCallback(async (hsnId) => {
    setError(null);
    
    try {
      // Get the HSN code details before deletion for the message
      const hsnToDelete = hsnCodes.find(hsn => hsn.id === hsnId);
      const hsnCodeValue = hsnToDelete?.hsn_code || hsnToDelete?.code || hsnId;
      
      const result = await hsnService.deleteHsnCode(hsnId);
      
      if (result.success) {
        // Remove the HSN code from local state
        setHsnCodes(prevHsnCodes => prevHsnCodes.filter(hsnCode => hsnCode.id !== hsnId));
        setTotalCount(prevCount => Math.max(0, prevCount - 1));
        
  // Show success message
  showDelete(i18n.t('hsnCodes.notifications.deleted', { code: hsnCodeValue }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.hsnCodes.deleteOne');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [hsnCodes, showDelete]);

  /**
   * Delete multiple HSN codes
   */
  const bulkDeleteHsnCodes = useCallback(async (hsnIds) => {
    if (!Array.isArray(hsnIds) || hsnIds.length === 0) {
      return { success: false, error: i18n.t('hookErrors.noSelection.hsnCodes') };
    }

    setError(null);
    
    try {
      const result = await hsnService.deleteHsnCodes(hsnIds);
      
      if (result.success) {
        // Remove the deleted HSN codes from local state
        setHsnCodes(prevHsnCodes => 
          prevHsnCodes.filter(hsnCode => !hsnIds.includes(hsnCode.id))
        );
        setTotalCount(prevCount => Math.max(0, prevCount - hsnIds.length));
        
        // Show success message
        const deletedCount = hsnIds.length;
  const bulkMsg = deletedCount > 1 ? i18n.t('hsnCodes.notifications.bulkDeleted', { count: String(deletedCount) }) : i18n.t('hsnCodes.notifications.bulkDeleted');
  showDelete(bulkMsg || `${deletedCount} HSN Code${deletedCount > 1 ? 's' : ''} ${deletedCount > 1 ? 'have' : 'has'} been deleted successfully`);
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || i18n.t('hookErrors.hsnCodes.deleteMany');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    hsnCodes,
    loading,
    error,
    totalPages,
    totalCount,
    
    // Actions
    fetchHsnCodes,
    getHsnCodeById,
    createHsnCode,
    updateHsnCode,
    deleteHsnCode,
    bulkDeleteHsnCodes,
    clearError,
  };
};

export default useHsnCodes;